/**
 * Add User Modal — create an admin user and assign permissions.
 * Permission assignment uses the shared, accessible <PermissionEditor /> (drag +
 * click toggles + bulk), so the Add and Edit flows behave identically.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  Alert,
  FormControlLabel,
  Checkbox,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { PERMISSION_RESOURCES } from '../../config/permissionResources';
import PermissionEditor, { PermissionMap, mapToPermissionsArray } from './PermissionEditor';

const emptyPermissionMap = (): PermissionMap => {
  const map: PermissionMap = {};
  PERMISSION_RESOURCES.forEach((r) => { map[r.id] = 'none'; });
  return map;
};

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddUserModal: React.FC<AddUserModalProps> = ({ open, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState<PermissionMap>(emptyPermissionMap);
  const [sendInvitation, setSendInvitation] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assigned = mapToPermissionsArray(permissions);
  const totalAssigned = assigned.length;

  const resetForm = () => {
    setEmail('');
    setName('');
    setPermissions(emptyPermissionMap());
    setSendInvitation(true);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!email) { setError('Email is required'); return; }
    if (totalAssigned === 0) { setError('Please assign at least one permission'); return; }

    setIsSubmitting(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, name, permissions: assigned, send_invitation: sendInvitation }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to create user');
      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: 1050, maxWidth: '95vw', borderRadius: '14px', overflow: 'hidden', maxHeight: '95vh' } }}
    >
      {/* Header */}
      <Box sx={{ background: 'linear-gradient(135deg, #1A5F7A 0%, #2980B9 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PersonAddIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Add New Admin User</Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.8 }}>Assign permissions by dragging or clicking</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip size="small" label={`${totalAssigned} Assigned`} sx={{ backgroundColor: totalAssigned > 0 ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)', color: '#fff', fontWeight: 600, fontSize: '0.72rem', height: 26 }} />
          <Chip size="small" label={`${PERMISSION_RESOURCES.length} Available`} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', fontWeight: 600, fontSize: '0.72rem', height: 26 }} />
          <IconButton onClick={onClose} sx={{ color: '#fff', ml: 0.5 }}><CloseIcon /></IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2.5, overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        {/* User info */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2.5, flexWrap: 'wrap' }}>
          <TextField label="Email Address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@trylifescribe.com" required size="small" sx={{ flex: 1, minWidth: 220 }} />
          <TextField label="Full Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" size="small" sx={{ flex: 1, minWidth: 220 }} />
          <FormControlLabel
            control={<Checkbox checked={sendInvitation} onChange={(e) => setSendInvitation(e.target.checked)} size="small" />}
            label={<Typography sx={{ fontSize: '0.82rem' }}>Send invitation email</Typography>}
            sx={{ mt: 0.5 }}
          />
        </Box>
        {sendInvitation && (
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.78rem' }}>
            User receives a Google OAuth login invite. Only permitted sidebar items will be visible.
          </Alert>
        )}

        {/* Permissions */}
        <PermissionEditor value={permissions} onChange={setPermissions} />
      </Box>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 1.5, backgroundColor: '#F8FAFC', borderTop: '1px solid', borderColor: 'grey.200' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, fontSize: '0.82rem' }}>
          {totalAssigned} of {PERMISSION_RESOURCES.length} resources assigned
        </Typography>
        <Button onClick={onClose} color="inherit" sx={{ mr: 1 }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!email || totalAssigned === 0 || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
          sx={{ background: 'linear-gradient(135deg, #1A5F7A 0%, #2980B9 100%)', fontWeight: 600, px: 3, '&:hover': { background: 'linear-gradient(135deg, #155367 0%, #2471A3 100%)' } }}
        >
          {isSubmitting ? 'Creating...' : 'Add Admin User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserModal;
